import type { EdtEntryInput, LocalEdtEntry } from '@iculogbook/shared';
import { uuidv7 } from 'uuidv7';

import { db } from '../db.ts';
import { ensurePersistentStorage } from '../persist.ts';
import {
  byOccurredAtDescending,
  isLive,
  notifyLocalWrite,
  outboxItem,
  userRange,
  writeWithOutbox,
} from './internal.ts';

/**
 * All reads and writes of EDT entries go through here, on exactly the same
 * terms as cases and procedures: components never touch Dexie, every write is
 * paired with an outbox item in one transaction, and nothing is ever hard
 * deleted.
 *
 * Simpler than the other two because an EDT entry references nothing. There is
 * no nesting, so no transaction spans two tables, and no delete has children to
 * take with it.
 */

export const edtRepository = {
  /** Writes a new entry and returns immediately. The network is not involved. */
  async create(userId: string, input: EdtEntryInput): Promise<LocalEdtEntry> {
    const now = new Date();

    const record: LocalEdtEntry = {
      ...input,
      id: uuidv7(),
      userId,
      source: 'manual',
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    };

    await writeWithOutbox('edt', record.id, userId, async () => {
      await db.edtEntries.add(record);
    });

    void ensurePersistentStorage();

    return record;
  },

  async update(userId: string, id: string, changes: Partial<EdtEntryInput>): Promise<void> {
    const existing = await db.edtEntries.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    await writeWithOutbox('edt', id, userId, async () => {
      await db.edtEntries.update(id, { ...changes, updatedAt: new Date(), syncedAt: null });
    });
  },

  /** Soft delete only. Nothing here removes a row. */
  async softDelete(userId: string, id: string): Promise<void> {
    const existing = await db.edtEntries.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    const now = new Date();

    await writeWithOutbox('edt', id, userId, async () => {
      await db.edtEntries.update(id, { deletedAt: now, updatedAt: now, syncedAt: null });
    });
  },

  async restore(userId: string, id: string): Promise<void> {
    const existing = await db.edtEntries.get(id);
    if (existing === undefined || existing.userId !== userId) return;

    await writeWithOutbox('edt', id, userId, async () => {
      await db.edtEntries.update(id, {
        deletedAt: undefined,
        updatedAt: new Date(),
        syncedAt: null,
      });
    });
  },

  /**
   * Writes entries back from an export, in one transaction, each with its own
   * outbox item so the restored logbook reaches the server too. Ids are
   * preserved, which is what makes a restore idempotent.
   */
  async restoreFromExport(userId: string, records: readonly LocalEdtEntry[]): Promise<number> {
    if (records.length === 0) return 0;

    await db.transaction('rw', db.edtEntries, db.outbox, async () => {
      for (const record of records) {
        await db.edtEntries.put({
          ...record,
          userId,
          source: 'restore',
          updatedAt: record.updatedAt,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('edt', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  /** Writes an imported batch, each entry queued for sync under one batch id. */
  async importBatch(
    userId: string,
    inputs: readonly EdtEntryInput[],
    importBatchId: string,
  ): Promise<number> {
    if (inputs.length === 0) return 0;

    const now = new Date();

    const records: LocalEdtEntry[] = inputs.map((input) => ({
      ...input,
      id: uuidv7(),
      userId,
      source: 'import',
      importBatchId,
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    }));

    await db.transaction('rw', db.edtEntries, db.outbox, async () => {
      for (const record of records) {
        await db.edtEntries.add(record);
        await db.outbox.add(outboxItem('edt', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  /** Soft-deletes every entry in one import batch. */
  async undoImport(userId: string, importBatchId: string): Promise<number> {
    const all = await db.edtEntries.toArray();
    const batch = all.filter(
      (record) =>
        record.userId === userId &&
        record.importBatchId === importBatchId &&
        record.deletedAt === undefined,
    );

    if (batch.length === 0) return 0;

    const now = new Date();

    await db.transaction('rw', db.edtEntries, db.outbox, async () => {
      for (const record of batch) {
        await db.edtEntries.update(record.id, { deletedAt: now, updatedAt: now, syncedAt: null });
        await db.outbox.add(outboxItem('edt', record.id, userId));
      }
    });

    notifyLocalWrite();
    return batch.length;
  },

  /** The import batches on this device, newest first, for the undo list. */
  async importBatches(
    userId: string,
  ): Promise<{ importBatchId: string; count: number; importedAt: Date }[]> {
    const all = await db.edtEntries.toArray();
    const batches = new Map<string, { count: number; importedAt: Date }>();

    for (const record of all) {
      const batchId = record.importBatchId;
      if (record.userId !== userId || batchId === undefined || record.deletedAt !== undefined) {
        continue;
      }

      const existing = batches.get(batchId);
      if (existing === undefined) batches.set(batchId, { count: 1, importedAt: record.createdAt });
      else existing.count += 1;
    }

    return [...batches.entries()]
      .map(([importBatchId, value]) => ({ importBatchId, ...value }))
      .sort((a, b) => b.importedAt.getTime() - a.importedAt.getTime());
  },

  async get(userId: string, id: string): Promise<LocalEdtEntry | undefined> {
    const record = await db.edtEntries.get(id);
    return record !== undefined && isLive(record, userId) ? record : undefined;
  },

  /** Newest first. Excludes deleted rows and anything belonging to another user. */
  async list(userId: string): Promise<LocalEdtEntry[]> {
    const [lower, upper] = userRange(userId);
    const records = await db.edtEntries
      .where('[userId+occurredAt]')
      .between(lower, upper, true, true)
      .toArray();

    return records.filter((record) => isLive(record, userId)).sort(byOccurredAtDescending);
  },

  /** Soft-deleted rows, for Settings → Recently deleted. */
  async listDeleted(userId: string): Promise<LocalEdtEntry[]> {
    const records = await db.edtEntries.toArray();

    return records
      .filter((record) => record.userId === userId && record.deletedAt !== undefined)
      .sort(byOccurredAtDescending);
  },

  /**
   * An unsaved copy, dated now — "the same weekly teaching session again".
   * Nothing is written; the copy becomes a record only when `create` is called
   * with it.
   */
  async duplicate(userId: string, id: string): Promise<EdtEntryInput | undefined> {
    const source = await this.get(userId, id);
    if (source === undefined) return undefined;

    return {
      activity: source.activity,
      title: source.title,
      occurredAt: new Date(),
      /*
       * The period is deliberately NOT copied. A duplicate is dated now, so
       * carrying the original's end date forward would produce a span running
       * backwards from today to some date in the past — which the schema then
       * rejects, on a form the trainee never filled in.
       */
      ...(source.durationMinutes === undefined
        ? {}
        : { durationMinutes: source.durationMinutes }),
      // Arrays are copied, not shared, or editing the duplicate would silently
      // edit the original's chips too.
      ...(source.hillos === undefined ? {} : { hillos: [...source.hillos] }),
      ...(source.note === undefined ? {} : { note: source.note }),
    };
  },
};
